import { ExecuteParameters, Model, ParameterId, VerbId } from '@tableau/api-internal-contract-js';
import * as TelemetryContract from '@tableau/telemetry-api-contract';
import * as UAParser from 'ua-parser-js';
import { DesktopTelemetryClient } from '../desktop-bootstrap/DesktopTemetryClient';

const supportedBrowsers = ['chrome', 'firefox', 'safari', 'edge'];

export const defaultErrorCode = 'unknown';

const GetTelemetryInstance = (() => {
  let instance: TelemetryContract.TelemetryClient;

  return () => {
    if (!instance) {
      // We are in server if the TelemetryContract global is implemented
      if (TelemetryContract) {
        instance = TelemetryContract.Instance;
      } else {
        instance = new DesktopTelemetryClient();
      }
    }

    return instance;
  };
})();

export function convertObjectToKeyValueArray(event: object): Array<string | boolean | number> {
  return Object.keys(event)
    .sort() // allows our order behavior to be consistent across calls / tests / platforms
    .map((k) => [k, event[k]])
    .reduce((arr, v) => {
      arr.push(v[0]);
      arr.push(v[1]);
      return arr;
    }, []);
}

export async function reportEvent(name: string, event: object): Promise<void> {
  const eventArr = convertObjectToKeyValueArray(event);

  GetTelemetryInstance().reportEvent(name, eventArr);
}

/**
 * Shared telemetry data
 */
export interface SharedInitializationFields {
  lang?: string;
  locale?: string;
  is_authoring?: boolean;
}

/**
 * Fields that are not meant to be set by callers
 * and are generated by the telemetry front end client.
 */
export interface InternalInitializationFields {
  browser?: string;
}

export interface TimingFields {
  network_runtime?: number;
  api_platform_runtime?: number;
}

export interface RunFields {
  run_id?: string;
  error_code?: string;
}

export interface InternalFunctionFields {
  function_name?: string;
  parameters?: string;
}

type FunctionFields = TimingFields & RunFields & InternalFunctionFields;

export class FunctionResults {
  public static Success(model: Model): FunctionResults {
    return new FunctionResults(model, undefined);
  }

  public static Error(errorMessage: string): FunctionResults {
    return new FunctionResults(undefined, errorMessage);
  }

  private constructor(private _model: Model | undefined, private _errorMessage: string | undefined) {}

  public isErrorResult(): boolean {
    return this._errorMessage !== undefined;
  }

  public isSuccessResult(): boolean {
    return this._model !== undefined;
  }

  public get errorMessage(): string {
    return this._errorMessage !== undefined ? this._errorMessage : 'No error';
  }

  public get model(): Model {
    return this._model !== undefined ? this._model : {};
  }
}

export class FunctionCallRuntimeData {
  public functionStartTimeMs?: number;
  public functionResponseTimeMs?: number;

  public constructor(functionStartTimeMs?: number) {
    this.functionStartTimeMs = functionStartTimeMs;
  }

  public getRuntimeCalculations(): TimingFields {
    const fields: TimingFields = {};

    const completeTime = new Date().getTime();

    if (this.functionStartTimeMs === undefined) {
      // initialize start was not called before complete - our runtime will be 0
      this.functionStartTimeMs = completeTime;
    }

    if (this.functionResponseTimeMs === undefined) {
      // with exceptions, there is no api_platform_runtime
      this.functionResponseTimeMs = completeTime;
    }

    // network runtime is response timestamp minus start time
    fields.network_runtime = this.functionResponseTimeMs - this.functionStartTimeMs;

    // platform runtime is total roundtrip less network time
    fields.api_platform_runtime = completeTime - this.functionStartTimeMs - fields.network_runtime;

    return fields;
  }
}

export function telemetryFunctionStart(): FunctionCallRuntimeData {
  return new FunctionCallRuntimeData(new Date().getTime());
}

export function telemetryFunctionResponse(callData: FunctionCallRuntimeData): void {
  callData.functionResponseTimeMs = new Date().getTime();
}

export function functionComplete(
  verb: VerbId,
  timing: TimingFields,
  parameters: ExecuteParameters,
  results: FunctionResults,
  runId: string,
): void {
  const fields: FunctionFields = timing;
  fields.run_id = runId;

  if (parameters[ParameterId.FunctionName]) {
    fields.function_name = parameters[ParameterId.FunctionName] as string;
  } else {
    fields.function_name = verb.toString();
  }

  // NOTE: Removing these because the ExecuteParameters are coerced to defaults
  //  before they get to this function call. Likely this will be changed in the future
  //  and this line can be uncommented.
  // fields.parameters = Object.keys(parameters).join(',');

  if (results.isErrorResult()) {
    // NOTE: We are not passing through an error code, because we don't have one at this
    //  point in time. For more fidelity in error information, we'll probably plumb through
    //  the error code from the error that was thrown on cpp side. It's a security issue
    //  to report the full error message, because it contains customer data.
    fields.error_code = defaultErrorCode;
  }

  reportEvent('DashboardFunctionCall', fields).then().catch();
}

export function getBrowser(): string {
  const browserInfo = new UAParser().setUA(navigator.userAgent).getBrowser();
  if (browserInfo.name && supportedBrowsers.includes(browserInfo.name.toLowerCase())) {
    return browserInfo.name.toLowerCase();
  } else {
    return 'other';
  }
}
